import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WarningIcon from '@mui/icons-material/Warning';

interface CreditInfo {
  availableCredits: number | null;  // null = unbegrenzt
  requiredCredits: number;
  remainingAfter: number | null;    // null = unbegrenzt
}

interface AnalysisCostDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  // Legacy props für Rückwärtskompatibilität
  estimatedCost?: number;
  analysisType: 'single' | 'multiple' | 'bulk';
  contractCount?: number;
  analyzableContractsCount?: number;
  breakdown?: {
    critical: number;
    nonCritical: number;
  };
  // Neue API-basierte Props
  loading?: boolean;
  error?: string | null;
  apiData?: {
    totalAnalyzableContracts: number;
    breakdown: {
      critical: number;
      nonCritical: number;
    };
    details?: {
      providers?: Array<{
        id: string;
        name: string;
        analyzableContracts: number;
      }>;
      contracts?: Array<{
        id: string;
        title: string;
        status: string;
        isCritical: boolean;
        isAnalyzable: boolean;
      }>;
    };
  } | null;
  // NEU: Credit-basierte Props
  creditInfo?: CreditInfo | null;
}

const AnalysisCostDialog: React.FC<AnalysisCostDialogProps> = ({
  open,
  onClose,
  onConfirm,
  analysisType,
  contractCount = 1,
  analyzableContractsCount,
  breakdown,
  loading = false,
  error = null,
  apiData = null,
  creditInfo = null
}) => {
  const { t } = useTranslation();
  
  const finalAnalyzableCount = apiData?.totalAnalyzableContracts ?? analyzableContractsCount ?? contractCount;
  const finalBreakdown = apiData?.breakdown ?? breakdown;

  const requiredCredits = creditInfo?.requiredCredits ?? finalAnalyzableCount;
  const availableCredits = creditInfo?.availableCredits;
  const remainingAfter = creditInfo?.remainingAfter ??
    (availableCredits !== null && availableCredits !== undefined
      ? Math.max(0, availableCredits - requiredCredits)
      : null);

  const hasEnoughCredits = availableCredits === null || availableCredits === undefined || availableCredits >= requiredCredits;
  const isUnlimited = availableCredits === null || availableCredits === undefined;

  const getAnalysisDescription = () => {
    const actualCount = finalAnalyzableCount;

    switch (analysisType) {
      case 'single':
        return t('common:analysisCostDialog.thisAnalysis');
      case 'multiple':
        return actualCount > 1 
          ? t('common:analysisCostDialog.analysisOfPlural', { count: actualCount })
          : t('common:analysisCostDialog.analysisOf', { count: actualCount });
      case 'bulk':
        if (apiData && apiData.totalAnalyzableContracts !== contractCount) {
          return t('common:analysisCostDialog.analysisFromProviders', { 
            contracts: apiData.totalAnalyzableContracts, 
            providers: contractCount 
          });
        } else if (analyzableContractsCount && analyzableContractsCount !== contractCount) {
          return t('common:analysisCostDialog.analysisFromProviders', { 
            contracts: analyzableContractsCount, 
            providers: contractCount 
          });
        }
        return contractCount > 1
          ? t('common:analysisCostDialog.bulkAnalysisPlural', { count: contractCount })
          : t('common:analysisCostDialog.bulkAnalysis', { count: contractCount });
      default:
        return t('common:analysisCostDialog.thisAnalysis');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 3,
        borderBottom: 1, borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {t('common:analysisCostDialog.title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'text.secondary' }}
          data-testid="analysis-cost-dialog-close-button"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {loading && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 4
          }}>
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {t('common:analysisCostDialog.loadingCredits')}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {/* Credit-Status Anzeige */}
            {/* Credit-Status Anzeige (Minimalist) */}

            {/* Bestätigung wenn genug Credits */}
            {hasEnoughCredits && !isUnlimited && (
              <Alert
                severity="info"
                sx={{ mb: 2, mt: 2 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {requiredCredits !== 1 
                    ? t('common:analysisCostDialog.creditsUsedPlural', { count: requiredCredits })
                    : t('common:analysisCostDialog.creditsUsed', { count: requiredCredits })}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {t('common:analysisCostDialog.available', { available: availableCredits, remaining: remainingAfter })}
                </Typography>
              </Alert>
            )}

            {/* Warnung wenn nicht genug Credits */}
            {!hasEnoughCredits && (
              <Alert
                severity="error"
                icon={<WarningAmberIcon />}
                sx={{ mb: 2, mt: 2 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t('common:analysisCostDialog.notEnoughCredits')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {requiredCredits !== 1 
                    ? t('common:analysisCostDialog.needCreditsPlural', { required: requiredCredits, available: availableCredits })
                    : t('common:analysisCostDialog.needCredits', { required: requiredCredits, available: availableCredits })}
                </Typography>
              </Alert>
            )}

            {/* Detaillierte Aufschlüsselung wenn verfügbar */}
            {finalBreakdown && (finalBreakdown.critical > 0 || finalBreakdown.nonCritical > 0) && (
              <Box sx={{
                p: 2,
                backgroundColor: 'grey.100',
                borderRadius: 1,
                border: 1, borderColor: 'divider',
                mb: 2
              }}>
                <Typography variant="body2" sx={{
                  color: 'text.primary',
                  fontWeight: 500,
                  mb: 1
                }}>
                  {t('common:analysisCostDialog.breakdown')}
                </Typography>
                {finalBreakdown.critical > 0 && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    • {finalBreakdown.critical > 1 
                      ? t('common:analysisCostDialog.criticalServices', { count: finalBreakdown.critical })
                      : t('common:analysisCostDialog.criticalService', { count: finalBreakdown.critical })}
                  </Typography>
                )}
                {finalBreakdown.nonCritical > 0 && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    • {finalBreakdown.nonCritical > 1 
                      ? t('common:analysisCostDialog.simpleServices', { count: finalBreakdown.nonCritical })
                      : t('common:analysisCostDialog.simpleService', { count: finalBreakdown.nonCritical })}
                  </Typography>
                )}
              </Box>
            )}

            <Typography variant="body2" sx={{
              color: 'text.secondary',
              textAlign: 'center',
              mb: 2,
              fontSize: '0.8rem'
            }}>
              {(apiData || (analyzableContractsCount && analyzableContractsCount !== contractCount)) && (
                <>{t('common:analysisCostDialog.onlyOpenOrFailed')} </>
              )}
              {t('common:analysisCostDialog.eachAnalysisCost')}
            </Typography>

            <Typography variant="body1" sx={{
              color: 'text.primary',
              textAlign: 'center',
              fontWeight: 500
            }}>
              {hasEnoughCredits
                ? t('common:analysisCostDialog.startAnalysisQuestion')
                : t('common:analysisCostDialog.contactAdmin')}
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{
        p: 3,
        gap: 3,
        borderTop: 1, borderColor: 'divider'
      }}>
        <span
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 4,
            color: 'primary.dark',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '1rem'
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', tabIndex: 0 }}
            onClick={onClose}
            data-testid="analysis-cost-dialog-cancel-button"
          >
            <CloseIcon sx={{ fontSize: '1.4rem' }} />
            {t('common:analysisCostDialog.cancel')}
          </Box>
        </span>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading || error !== null || finalAnalyzableCount === 0 || !hasEnoughCredits}
          data-testid="analysis-cost-dialog-confirm-button"
        >
          {loading ? t('common:analysisCostDialog.loading') :
            error ? t('common:analysisCostDialog.errorOccurred') :
              !hasEnoughCredits ? t('common:analysisCostDialog.notEnoughCreditsButton') :
                finalAnalyzableCount === 0 ? t('common:analysisCostDialog.noAnalysisPossible') : t('common:analysisCostDialog.startAnalysis')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnalysisCostDialog; 